/**
 * Blog Service
 *
 * Handles loading, parsing, and processing of Markdown-based blog posts.
 * Uses gray-matter for frontmatter parsing, marked for markdown rendering,
 * and highlight.js for code syntax highlighting.
 */

import matter from 'gray-matter';
import { marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
import hljs from 'highlight.js';
import type {
  BlogPost,
  BlogPaginatedResult,
  TOCItem,
} from '../types/blog';

/**
 * Configure marked with highlight.js for code syntax highlighting
 */
marked.use(
  markedHighlight({
    emptyLangClass: 'hljs',
    langPrefix: 'hljs language-',
    highlight(code: string, lang: string) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return hljs.highlight(code, { language: lang }).value;
        } catch (err) {
          console.error('Highlighting error:', err);
        }
      }
      return hljs.highlightAuto(code).value;
    },
  })
);

marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Load all blog post markdown files using Vite's glob import
 */
const blogPostFiles = import.meta.glob('/content/blog/*.md', { as: 'raw', eager: true });

/**
 * Parse a single markdown file into a BlogPost
 */
function parseMarkdownFile(filePath: string, content: string): BlogPost | null {
  try {
    const { data: frontmatter, content: markdownContent } = matter(content);

    // Validate required frontmatter fields
    if (!frontmatter.title || !frontmatter.slug) {
      console.error(`Missing required frontmatter in ${filePath}`);
      return null;
    }

    // Convert markdown to HTML
    const htmlContent = marked.parse(markdownContent) as string;

    // Auto-generate excerpt from content (first 200 chars of plain text)
    const plainText = markdownContent.replace(/[#*`[\]()]/g, '').trim();
    const excerpt = frontmatter.excerpt || plainText.substring(0, 200) + '...';

    // Calculate read time if not provided (average reading speed: 200 words/min)
    const readTime =
      frontmatter.readTime ||
      Math.ceil(markdownContent.split(/\s+/).length / 200);

    const blogPost: BlogPost = {
      title: frontmatter.title,
      slug: frontmatter.slug,
      description: frontmatter.description || excerpt,
      author: frontmatter.author || 'Kenrick Goldson',
      authorBio:
        frontmatter.authorBio ||
        'Principal Engineer at KG ProDesign with 15+ years building enterprise software',
      authorPhoto: frontmatter.authorPhoto || '/images/team/kenrick.jpg',
      authorSocial: frontmatter.authorSocial || {},
      publishDate: frontmatter.publishDate,
      updatedDate: frontmatter.updatedDate,
      readTime,
      category: frontmatter.category || 'Uncategorized',
      tags: frontmatter.tags || [],
      heroImage: frontmatter.heroImage || '/images/blog/default.png',
      featured: frontmatter.featured || false,
      draft: frontmatter.draft || false,
      seo: frontmatter.seo,
      content: htmlContent,
      excerpt,
    };

    return blogPost;
  } catch (error) {
    console.error(`Error parsing markdown file ${filePath}:`, error);
    return null;
  }
}

/**
 * Load all blog posts from markdown files
 * Filters out drafts and sorts by publish date (newest first)
 */
export async function getBlogPosts(): Promise<BlogPost[]> {
  const posts: BlogPost[] = [];

  for (const [filePath, content] of Object.entries(blogPostFiles)) {
    if (typeof content === 'string') {
      const post = parseMarkdownFile(filePath, content);
      if (post && !post.draft) {
        posts.push(post);
      }
    }
  }

  // Sort by publish date, newest first
  return posts.sort(
    (a, b) =>
      new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );
}

/**
 * Get a single blog post by slug
 */
export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  const allPosts = await getBlogPosts();
  return allPosts.find((post) => post.slug === slug) || null;
}

/**
 * Paginate blog posts
 */
export function getPaginatedPosts(
  posts: BlogPost[],
  page: number = 1,
  perPage: number = 12
): BlogPaginatedResult {
  const totalPosts = posts.length;
  const totalPages = Math.ceil(totalPosts / perPage);
  const currentPage = Math.max(1, Math.min(page, totalPages));

  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedPosts = posts.slice(startIndex, endIndex);

  return {
    posts: paginatedPosts,
    currentPage,
    totalPages,
    totalPosts,
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1,
  };
}

/**
 * Filter posts by category
 */
export function filterPostsByCategory(
  posts: BlogPost[],
  category: string
): BlogPost[] {
  if (!category || category.toLowerCase() === 'all') {
    return posts;
  }
  return posts.filter(
    (post) => post.category.toLowerCase() === category.toLowerCase()
  );
}

/**
 * Filter posts by tag
 */
export function filterPostsByTag(posts: BlogPost[], tag: string): BlogPost[] {
  if (!tag) {
    return posts;
  }
  return posts.filter((post) =>
    post.tags.some((t) => t.toLowerCase() === tag.toLowerCase())
  );
}

/**
 * Get featured posts (posts with featured: true)
 */
export function getFeaturedPosts(
  posts: BlogPost[],
  limit: number = 3
): BlogPost[] {
  return posts.filter((post) => post.featured).slice(0, limit);
}

/**
 * Get related posts based on category and tags
 */
export function getRelatedPosts(
  currentPost: BlogPost,
  allPosts: BlogPost[],
  limit: number = 3
): BlogPost[] {
  // Filter out the current post
  const otherPosts = allPosts.filter((post) => post.slug !== currentPost.slug);

  // Score posts by relevance
  const scoredPosts = otherPosts.map((post) => {
    let score = 0;

    // Same category: +10 points
    if (post.category === currentPost.category) {
      score += 10;
    }

    // Matching tags: +2 points per tag
    const matchingTags = post.tags.filter((tag) =>
      currentPost.tags.includes(tag)
    );
    score += matchingTags.length * 2;

    return { post, score };
  });

  // Sort by score (highest first) and return top posts
  return scoredPosts
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item) => item.post);
}

/**
 * Get unique categories from all posts
 */
export function getUniqueCategories(posts: BlogPost[]): string[] {
  const categories = new Set(posts.map((post) => post.category));
  return Array.from(categories).sort();
}

/**
 * Get unique tags from all posts
 */
export function getUniqueTags(posts: BlogPost[]): string[] {
  const tags = new Set(posts.flatMap((post) => post.tags));
  return Array.from(tags).sort();
}

/**
 * Calculate estimated read time based on word count
 * Average reading speed: 200 words per minute
 */
export function calculateReadTime(content: string): number {
  const words = content.trim().split(/\s+/).length;
  return Math.ceil(words / 200);
}

/**
 * Generate table of contents from markdown content
 * Extracts h2 and h3 headings
 */
export function generateTableOfContents(content: string): TOCItem[] {
  const toc: TOCItem[] = [];
  const headingRegex = /^(#{2,3})\s+(.+)$/gm;
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length; // 2 for ##, 3 for ###
    const text = match[2].trim();
    const id = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');

    toc.push({
      id,
      text,
      level,
    });
  }

  return toc;
}

/**
 * Search posts by title, description, or content
 */
export function searchPosts(posts: BlogPost[], query: string): BlogPost[] {
  if (!query.trim()) {
    return posts;
  }

  const lowerQuery = query.toLowerCase();

  return posts.filter(
    (post) =>
      post.title.toLowerCase().includes(lowerQuery) ||
      post.description.toLowerCase().includes(lowerQuery) ||
      post.content.toLowerCase().includes(lowerQuery) ||
      post.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Get posts by date range
 */
export function getPostsByDateRange(
  posts: BlogPost[],
  startDate: Date,
  endDate: Date
): BlogPost[] {
  return posts.filter((post) => {
    const postDate = new Date(post.publishDate);
    return postDate >= startDate && postDate <= endDate;
  });
}

/**
 * Get recent posts
 */
export function getRecentPosts(
  posts: BlogPost[],
  limit: number = 5
): BlogPost[] {
  return posts.slice(0, limit);
}
